const CHUNK_RELOAD_KEY = 'falaya:chunk-reload';

function collectErrorMessages(error: unknown): string[] {
  const messages: string[] = [];
  const seen = new Set<unknown>();
  let current: unknown = error;

  while (current != null && !seen.has(current)) {
    seen.add(current);

    if (typeof current === 'string') {
      messages.push(current);
      break;
    }

    if (current instanceof Error) {
      messages.push(current.message);
      if (current.name) {
        messages.push(current.name);
      }
      current =
        (current as Error & { cause?: unknown; originalError?: unknown }).cause ??
        (current as Error & { originalError?: unknown }).originalError;
      continue;
    }

    if (typeof current === 'object' && 'message' in current) {
      messages.push(String((current as { message: unknown }).message));
    }
    break;
  }

  return messages;
}

export function isChunkLoadError(error: unknown): boolean {
  return collectErrorMessages(error).some((message) =>
    /ChunkLoadError/i.test(message) ||
    /Loading chunk [\da-f]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Cannot find module '\d+'/i.test(message)
  );
}

/** Reload once when lazy chunks from a previous deploy fail to load. */
export function reloadPageForStaleChunks(): boolean {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false;
  }

  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'pending') {
    return false;
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, 'pending');
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag(): void {
  sessionStorage?.removeItem(CHUNK_RELOAD_KEY);
}

export function registerChunkLoadRecoveryHandlers(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkLoadError(event.reason)) {
      return;
    }
    if (reloadPageForStaleChunks()) {
      event.preventDefault();
    }
  });

  window.addEventListener('error', (event) => {
    const error = event.error ?? event.message;
    if (!isChunkLoadError(error)) {
      return;
    }
    if (reloadPageForStaleChunks()) {
      event.preventDefault();
    }
  });
}
